import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { 
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowLeftIcon,
  DocumentArrowDownIcon
} from '@heroicons/react/24/outline';
import { apiService } from '../services/apiService';
import { authService } from '../services/authService';
import { downloadFile } from '../utils/exportUtils';
import { useUser } from '../contexts/UserContext';

const SlaReport = () => {
  const { isAdmin } = useUser();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(null);

  useEffect(() => {
    const loadTeams = async () => {
      try {
        const userId = isAdmin() ? null : authService.getUserId();
        const teamsData = await apiService.getTeams(userId);
        setTeams(teamsData);
      } catch (err) {
        console.error('Failed to load teams:', err);
      }
    };
    loadTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadSlaReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTeamId]);

  const [exporting, setExporting] = useState(false);

  const loadSlaReport = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getSlaReport(selectedTeamId);
      setReport(data);
    } catch (err) {
      console.error('Failed to load SLA report:', err);
      setError('Failed to load SLA report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleExportToExcel = async () => {
    try {
      setExporting(true);
      const blob = await apiService.exportSlaReportToExcel();
      const filename = `SLA_Report_${new Date().toISOString().slice(0, 10)}.xlsx`;
      downloadFile(blob, filename);
    } catch (err) {
      console.error('Failed to export to Excel:', err);
      setError('Failed to export report to Excel. Please try again.');
    } finally {
      setExporting(false);
    }
  };



  const formatTime = (minutes) => {
    if (minutes < 60) {
      return `${Math.round(minutes)} min`;
    } else if (minutes < 1440) {
      const hours = Math.floor(minutes / 60);
      const mins = Math.round(minutes % 60);
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    } else {
      const days = Math.floor(minutes / 1440);
      const hours = Math.floor((minutes % 1440) / 60);
      return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical': return '#dc2626';
      case 'High': return '#ea580c';
      case 'Medium': return '#ca8a04';
      case 'Low': return '#16a34a';
      default: return '#6b7280';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="text-center text-gray-500">
        No SLA report data available.
      </div>
    );
  }

  // Prepare data for charts
  const chartData = report.metrics.map(metric => ({
    name: metric.categoryName,
    'Met SLA': metric.metSla,
    'Breached SLA': metric.breachedSla,
    'Total Tickets': metric.totalTickets,
    'Compliance Rate': Math.round(metric.slaCompliancePercentage)
  }));

  const pieData = [
    { name: 'Met SLA', value: report.summary.metSla, color: '#16a34a' },
    { name: 'Breached SLA', value: report.summary.breachedSla, color: '#dc2626' }
  ];

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-3">
            <Link
              to="/reports"
              className="flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-1" />
              Back to Reports
            </Link>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleExportToExcel}
              disabled={exporting}
              className="flex items-center px-3 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
            >
              <DocumentArrowDownIcon className="h-4 w-4 mr-1" />
              {exporting ? 'Exporting...' : 'Export Excel'}
            </button>

          </div>
        </div>
        <h1 className="text-2xl font-bold text-gray-900">SLA Performance Report</h1>
        <div className="flex items-center space-x-4 mt-1">
          <p className="text-gray-600">
            Report generated on {new Date(report.reportDate).toLocaleDateString()}
          </p>
          {teams.length > 0 && (
            <select
              value={selectedTeamId ?? ''}
              onChange={(e) => setSelectedTeamId(e.target.value ? parseInt(e.target.value) : null)}
              className="px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Teams</option>
              {teams.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <ClockIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Tickets</p>
              <p className="text-2xl font-bold text-gray-900">{report.summary.totalTickets}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircleIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Met SLA</p>
              <p className="text-2xl font-bold text-green-600">{report.summary.metSla}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <XCircleIcon className="h-6 w-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Breached SLA</p>
              <p className="text-2xl font-bold text-red-600">{report.summary.breachedSla}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircleIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Closed Tickets SLA Compliance</p>
              <p className="text-2xl font-bold text-green-600">
                {report.summary.closedTicketsSlaCompliance !== undefined 
                  ? Math.round(report.summary.closedTicketsSlaCompliance) 
                  : Math.round((report.summary.metSla / (report.summary.metSla + report.summary.breachedSla)) * 100)}%
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <ClockIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Point-in-Time SLA Compliance</p>
              <p className="text-2xl font-bold text-blue-600">
                {report.summary.pointInTimeSlaCompliance !== undefined 
                  ? Math.round(report.summary.pointInTimeSlaCompliance) 
                  : Math.round((report.summary.metSla / report.summary.totalTickets) * 100)}%
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <ClockIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">In Progress (Within SLA)</p>
              <p className="text-2xl font-bold text-blue-600">
                {report.summary.inProgressWithinSla || 0}
              </p>
            </div>
          </div>
        </div>


      </div>

      {/* SLA Compliance Explanation */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">SLA Compliance Metrics Explained</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="border-l-4 border-green-500 pl-4">
            <h4 className="font-medium text-gray-900 mb-2">Closed Tickets SLA Compliance</h4>
            <p className="text-sm text-gray-600 mb-2">
              Formula: <span className="font-mono">Met SLA / (Met SLA + Breached SLA)</span>
            </p>
            <p className="text-sm text-gray-600">
              Only includes tickets that are fully closed. This metric shows the historical performance 
              of completed tickets and is most commonly used for SLA reporting.
            </p>
          </div>
          <div className="border-l-4 border-blue-500 pl-4">
            <h4 className="font-medium text-gray-900 mb-2">Point-in-Time SLA Compliance</h4>
            <p className="text-sm text-gray-600 mb-2">
              Formula: <span className="font-mono">(Met SLA + In Progress within SLA) / Total Tickets</span>
            </p>
            <p className="text-sm text-gray-600">
              Includes all tickets, including those still in progress if they are within SLA. 
              This provides a current snapshot of overall SLA performance.
            </p>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">SLA Compliance by Priority</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="Met SLA" fill="#16a34a" />
              <Bar dataKey="Breached SLA" fill="#dc2626" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Overall SLA Performance</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Detailed Metrics Table */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Detailed SLA Metrics by Priority</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Priority
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Tickets
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Met SLA
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Breached SLA
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Compliance Rate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Expected Resolution
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actual Avg Resolution
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Performance
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {report.metrics.map((metric) => (
                <tr key={metric.priority} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span 
                      className="inline-flex px-2 py-1 text-xs font-semibold rounded-full text-white"
                      style={{ backgroundColor: getPriorityColor(metric.priority) }}
                    >
                      {metric.categoryName}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {metric.totalTickets}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">
                    {metric.metSla}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-medium">
                    {metric.breachedSla}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span className={`font-medium ${
                      metric.slaCompliancePercentage >= 90 ? 'text-green-600' :
                      metric.slaCompliancePercentage >= 70 ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                      {Math.round(metric.slaCompliancePercentage)}%
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatTime(metric.expectedResolutionTimeMinutes)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatTime(metric.averageResolutionTimeMinutes)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {metric.isMeetingResolutionSla ? (
                        <CheckCircleIcon className="h-5 w-5 text-green-500" />
                      ) : (
                        <XCircleIcon className="h-5 w-5 text-red-500" />
                      )}
                      <span className={`ml-2 text-sm font-medium ${
                        metric.isMeetingResolutionSla ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {metric.isMeetingResolutionSla ? 'Meeting' : 'Breaching'}
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SlaReport; 